import { motion } from 'motion/react';
import { AnimatePresence } from 'motion/react';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { ChevronRight } from 'lucide-react';
import { SPORT_EMOJIS } from '@/types/sport';
import type { MatchJournal } from '@/types/matchJournal';
import { StatusBadge } from './StatusBadge';
import { UnreadCommentBadge } from './UnreadCommentBadge';

interface MatchJournalCardProps {
  journal: MatchJournal;
  onPress: (id: string) => void;
  onPostNotePress?: (id: string) => void;
  variant?: 'timeline' | 'mini';
}

const STATUS_THEME = {
  pre: {
    bar:       'bg-gradient-to-r from-sky-400 to-sky-500',
    barShadow: 'shadow-[0_0_8px_rgba(14,165,233,0.5)]',
    glow:      'from-sky-500/12',
  },
  completed: {
    bar:       'bg-gradient-to-r from-emerald-400 to-emerald-500',
    barShadow: 'shadow-[0_0_8px_rgba(52,211,153,0.5)]',
    glow:      'from-emerald-500/12',
  },
  post_only: {
    bar:       'bg-gradient-to-r from-sky-600 to-sky-700',
    barShadow: '',
    glow:      'from-sky-700/8',
  },
} as const;

const RESULT_LABEL: Record<string, { label: string; cls: string }> = {
  win:  { label: '勝', cls: 'text-green-400 bg-green-500/15' },
  draw: { label: '分', cls: 'text-zinc-400 bg-zinc-700/50' },
  loss: { label: '負', cls: 'text-red-400 bg-red-500/15' },
};

export function MatchJournalCard({ journal, onPress, onPostNotePress }: MatchJournalCardProps) {
  const dateStr = format(journal.date.toDate(), 'M/d（EEE）', { locale: ja });
  const sportEmoji = SPORT_EMOJIS[journal.sport];
  const theme = STATUS_THEME[journal.status as keyof typeof STATUS_THEME] ?? STATUS_THEME.pre;
  const resultInfo = journal.postNote?.result ? RESULT_LABEL[journal.postNote.result] : null;
  const totalGoals = journal.preNote?.goals.length ?? 0;
  const achievedCount = journal.postNote?.goalReviews.filter(
    (r) => r.achievement === 'achieved' || r.achievement === 'partial'
  ).length ?? 0;
  const achievementRate = totalGoals > 0 ? Math.round((achievedCount / totalGoals) * 100) : 0;

  return (
    <motion.article
      aria-label={`${dateStr} ${journal.opponent}戦`}
      whileTap={{ scale: 0.97 }}
      onClick={() => onPress(journal.id)}
      className="relative bg-zinc-900 border border-zinc-800/60 rounded-xl overflow-visible cursor-pointer active:bg-zinc-800/80 transition-all duration-150 hover:border-zinc-700 hover:shadow-lg hover:shadow-black/20"
    >
      {/* 未読コメントバッジ */}
      <AnimatePresence>
        {(journal.unreadCommentCount ?? 0) > 0 && (
          <UnreadCommentBadge count={journal.unreadCommentCount} />
        )}
      </AnimatePresence>

      {/* カラーバー */}
      <div className={`h-1.5 w-full rounded-t-xl ${theme.bar} ${theme.barShadow}`} />

      <div className={`bg-gradient-to-b ${theme.glow} to-transparent p-3 flex flex-col gap-2`}>
        {/* 日付・スポーツ */}
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-zinc-500 flex items-center gap-1">
            <span>{sportEmoji}</span>{dateStr}
          </span>
          <StatusBadge status={journal.status} />
        </div>

        {/* 対戦相手 */}
        <div>
          <p className="text-[10px] text-[var(--color-brand-primary)]/60 font-bold uppercase tracking-wider mb-0.5">vs</p>
          <p className="text-sm font-bold text-white truncate">{journal.opponent}</p>
        </div>

        {/* 完了時: スコア＋達成率 */}
        {journal.status !== 'pre' && journal.postNote && (
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              {resultInfo && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${resultInfo.cls}`}>
                  {resultInfo.label}
                </span>
              )}
              <span className="text-xl font-black text-white tabular-nums">
                {journal.postNote.myScore ?? '-'}
              </span>
              <span className="text-xs text-zinc-600">-</span>
              <span className="text-lg font-black text-zinc-500 tabular-nums">
                {journal.postNote.opponentScore ?? '-'}
              </span>
              {journal.postNote.performance && (
                <span className="ml-auto text-[10px] text-amber-400">
                  {'★'.repeat(journal.postNote.performance)}
                </span>
              )}
            </div>
            {totalGoals > 0 && (
              <div>
                <div className="h-1 rounded-full bg-zinc-800 overflow-hidden">
                  <div className="h-full rounded-full bg-green-500" style={{ width: `${achievementRate}%` }} />
                </div>
                <p className="text-[10px] text-zinc-600 mt-0.5">{achievedCount}/{totalGoals}目標達成</p>
              </div>
            )}
          </div>
        )}

        {/* 試合前: 振り返りCTA */}
        {journal.status === 'pre' && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onPostNotePress?.(journal.id); }}
            className="w-full mt-1 bg-[var(--color-brand-primary)]/12 border border-[var(--color-brand-primary)]/40 hover:bg-[var(--color-brand-primary)]/20 transition-colors rounded-lg py-1.5 text-[11px] text-[var(--color-brand-primary)] font-semibold flex items-center justify-center gap-0.5"
          >
            振り返りを書く <ChevronRight size={12} />
          </button>
        )}
      </div>
    </motion.article>
  );
}
